import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, FileText, Check } from '../components/Icons.jsx';

const settings = [
  { label: 'Hospital ward', emoji: '🏥' },
  { label: 'A&E', emoji: '🚑' },
  { label: 'Surgical theatre', emoji: '🩻' },
  { label: 'GP surgery', emoji: '🩺' },
  { label: 'Hospice / palliative', emoji: '💚' },
  { label: 'Mental health', emoji: '🧠' },
  { label: 'Care home volunteering', emoji: '👵' },
  { label: 'Virtual session', emoji: '💻' }
];

const gmcOutcomes = [
  { id: 'clinician', label: 'Professional knowledge & clinical skill', emoji: '🩻', hint: 'Patient assessment, examination, diagnosis, the science behind care.' },
  { id: 'professional', label: 'Professional values & behaviours', emoji: '🤝', hint: 'Honesty, confidentiality, respecting dignity, working in a team.' },
  { id: 'safety', label: 'Patient safety & quality improvement', emoji: '🛡️', hint: 'Hygiene, escalation, reporting concerns, learning from mistakes.' },
  { id: 'scholar', label: 'Lifelong learning & teaching', emoji: '📚', hint: 'Reflecting, asking questions, evidence-based decisions, sharing knowledge.' }
];

const steps = ['Placement', 'What happened?', 'So what?', 'Now what?', 'Your paragraph'];

export default function WeReflection() {
  const [step, setStep] = useState(0);
  const [setting, setSetting] = useState(settings[0].label);
  const [date, setDate] = useState('');
  const [outcome, setOutcome] = useState(gmcOutcomes[0].id);
  const [what, setWhat] = useState('');
  const [soWhat, setSoWhat] = useState('');
  const [nowWhat, setNowWhat] = useState('');

  const outcomeObj = gmcOutcomes.find((o) => o.id === outcome);

  const paragraph = useMemo(() => {
    if (!what.trim()) return '';
    const opening = `During my time in ${setting.toLowerCase()}${date ? ` in ${date}` : ''},`;
    const body1 = what.trim();
    const body2 = soWhat.trim() ? ` This taught me that ${soWhat.trim().replace(/^[A-Z]/, (c) => c.toLowerCase())}` : '';
    const body3 = nowWhat.trim() ? ` Going forward, ${nowWhat.trim().replace(/^[A-Z]/, (c) => c.toLowerCase())}` : '';
    const tag = ` — an experience that sharpened my appreciation of ${outcomeObj.label.toLowerCase()}.`;
    return `${opening} ${body1.replace(/^[A-Z]/, (c) => c.toLowerCase())}.${body2 ? body2 + '.' : ''}${body3 ? body3 + '.' : ''}${tag}`;
  }, [setting, date, what, soWhat, nowWhat, outcomeObj]);

  const canAdvance = step === 0 ? true : step === 1 ? !!what.trim() : step === 2 ? true : step === 3 ? true : true;
  const next = () => setStep(Math.min(steps.length - 1, step + 1));
  const back = () => setStep(Math.max(0, step - 1));
  const copyOut = () => { navigator.clipboard?.writeText(paragraph); };

  return (
    <>
      <div className="page-head" style={{ marginBottom: 14 }}>
        <Link to="/work-experience" className="back-link"><ArrowLeft width="16" height="16" />Back to Work experience</Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>📝 {steps[step]}</span>
        <span>Step {step + 1} of {steps.length}</span>
      </div>
      <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ width: `${((step + 1) / steps.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', transition: 'width 250ms ease' }} />
      </div>

      <div key={step} className="card" style={{ animation: 'fadeUp 280ms ease', padding: '28px 28px 24px', marginBottom: 16 }}>
        {step === 0 && (
          <>
            <h1 style={{ margin: '0 0 18px', fontSize: 'clamp(20px, 4vw, 26px)', letterSpacing: '-0.01em' }}>Where was the placement?</h1>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 22 }}>
              {settings.map((s) => {
                const sel = setting === s.label;
                return (
                  <button key={s.label} onClick={() => setSetting(s.label)} style={chipBtn(sel)}>
                    <span style={{ fontSize: 22 }}>{s.emoji}</span>
                    <span style={{ flex: 1 }}>{s.label}</span>
                    {sel && <Check width="16" height="16" />}
                  </button>
                );
              })}
            </div>

            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>When (optional)</label>
            <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="e.g. Jul 2026" style={{ display: 'block', width: '100%', maxWidth: 260, padding: '10px 12px', fontSize: 15, border: '1px solid var(--border-strong)', borderRadius: 10, marginTop: 6, marginBottom: 22 }} />

            <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>Which GMC outcome did it touch most?</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {gmcOutcomes.map((o) => {
                const sel = outcome === o.id;
                return (
                  <button key={o.id} onClick={() => setOutcome(o.id)} style={{ ...chipBtn(sel), alignItems: 'flex-start', flexDirection: 'column', textAlign: 'left' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 22 }}>{o.emoji}</span><strong>{o.label}</strong></span>
                    <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)', marginTop: 4 }}>{o.hint}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 1 && (
          <Prompt
            title="What happened?"
            sub="One specific moment. Not a summary of the week. Who was there, what did you observe, what did you do."
            example="I observed a junior doctor break bad news to a patient's family in the side room…"
            value={what} onChange={setWhat}
          />
        )}
        {step === 2 && (
          <Prompt
            title="So what?"
            sub="Why it mattered to you. What it revealed about being a doctor. What surprised you."
            example="The structure of the conversation — pausing, checking understanding — was harder than I'd expected."
            value={soWhat} onChange={setSoWhat}
          />
        )}
        {step === 3 && (
          <Prompt
            title="Now what?"
            sub="What you'll do differently. Specific. Not 'I want to be a doctor'."
            example="I started a communication-skills logbook and joined a hospice befriender programme."
            value={nowWhat} onChange={setNowWhat}
          />
        )}
        {step === 4 && (
          <>
            <h1 style={{ margin: '0 0 8px', fontSize: 'clamp(20px, 4vw, 26px)' }}>Your paragraph</h1>
            <p style={{ margin: '0 0 18px', color: 'var(--text-muted)', fontSize: 14 }}>Copy this straight into your PS drafts doc, or tweak it inline.</p>
            <div style={{ padding: '22px 24px', background: 'var(--brand-deep)', color: '#fff', borderRadius: 14, fontSize: 17, lineHeight: 1.65 }}>
              {paragraph || 'You need to fill in at least "What happened?" — go back to step 2.'}
            </div>
            {paragraph && (
              <button onClick={copyOut} className="btn btn-primary" style={{ marginTop: 16 }}>Copy paragraph <FileText width="14" height="14" /></button>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <button onClick={back} disabled={step === 0} style={{
          background: 'none', border: 'none', cursor: step === 0 ? 'not-allowed' : 'pointer',
          color: step === 0 ? 'var(--text-muted)' : 'var(--text-2)', fontSize: 14, fontWeight: 600,
          opacity: step === 0 ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6
        }}><ArrowLeft width="14" height="14" />Back</button>

        {step < steps.length - 1 ? (
          <button onClick={next} disabled={!canAdvance} className="btn btn-primary" style={{ opacity: canAdvance ? 1 : 0.5 }}>
            Next <ArrowRight width="14" height="14" />
          </button>
        ) : (
          <Link to="/career-camp" className="btn btn-secondary">Done — see Career Camp <ArrowRight width="14" height="14" /></Link>
        )}
      </div>

      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </>
  );
}

function Prompt({ title, sub, example, value, onChange }) {
  return (
    <>
      <h1 style={{ margin: '0 0 8px', fontSize: 'clamp(20px, 4vw, 26px)', letterSpacing: '-0.01em' }}>{title}</h1>
      <p style={{ margin: '0 0 18px', color: 'var(--text-muted)', fontSize: 15 }}>{sub}</p>
      <textarea
        value={value} onChange={(e) => onChange(e.target.value)} placeholder={example}
        rows={5}
        style={{ width: '100%', padding: '14px 16px', fontSize: 16, lineHeight: 1.5, border: '2px solid var(--border)', borderRadius: 14, fontFamily: 'inherit', resize: 'vertical', transition: 'border-color 150ms ease' }}
        onFocus={(e) => e.currentTarget.style.borderColor = 'var(--brand-purple)'}
        onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
        autoFocus
      />
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{value.trim().split(/\s+/).filter(Boolean).length} words</div>
    </>
  );
}

function chipBtn(selected) {
  return {
    padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
    borderRadius: 12, fontSize: 14, fontWeight: selected ? 600 : 500, cursor: 'pointer',
    border: `2px solid ${selected ? 'var(--brand-purple)' : 'var(--border)'}`,
    background: selected ? 'var(--accent-soft)' : '#fff',
    color: 'var(--text)',
    transition: 'border-color 180ms ease, background 180ms ease'
  };
}
